import Loader from '@shared/loader'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import ResponsiveDialog from '../responsive-dialog'
import Toolbar from '../toolbar'
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material'
import { StoreSummary } from '@types'
import { getStoreList } from '@queries/stores'
import { makeStyles } from 'tss-react/mui'
import { useQuery } from '@tanstack/react-query'

const ProductStoresDialog = ({
  existingStoreIds,
  loading,
  onClose,
  onConfirm,
  open,
}: Props): ReactElement => {
  const { classes } = useStyles()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')

  const { isLoading: loadingStores, data: stores } = useQuery({
    queryKey: ['storeList'],
    queryFn: getStoreList,
    staleTime: 30000,
  })

  const regions = useMemo(() => {
    if (!stores) return []

    const term = search.trim().toLowerCase()
    const matching = term
      ? stores.filter(
          (store) =>
            store.name.toLowerCase().includes(term) ||
            store.region.toLowerCase().includes(term),
        )
      : stores

    return groupByRegion(matching)
  }, [stores, search])

  const toggleStore = (storeId: number) => (_, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current)
      if (checked) {
        next.add(storeId)
      } else {
        next.delete(storeId)
      }
      return next
    })
  }

  const toggleRegion =
    (regionStores: StoreSummary[]) => (_, checked: boolean) => {
      setSelected((current) => {
        const next = new Set(current)
        regionStores.forEach((store) =>
          checked ? next.add(store.id) : next.delete(store.id),
        )
        return next
      })
    }

  // Refresh on open and convert the values to a set since we're adding and removing elements
  // repeatedly
  useEffect(() => {
    if (open) {
      setSelected(new Set(existingStoreIds))
      setSearch('')
    }
  }, [open])

  const toAdd = Array.from(selected).filter(
    (id) => !existingStoreIds.includes(id),
  )
  const toRemove = existingStoreIds.filter((id) => !selected.has(id))
  const hasChanges = toAdd.length > 0 || toRemove.length > 0

  const handleConfirm = () => onConfirm(Array.from(selected))

  const handleSearchChanged = (event) => setSearch(event.target.value)

  return (
    <ResponsiveDialog
      fullWidth
      loading={loading}
      maxWidth='lg'
      onClose={onClose}
      open={open}
    >
      <Toolbar title='Edit On Store Menus' />
      <DialogContent>
        <TextField
          className={classes.search}
          fullWidth
          label='Filter stores'
          onChange={handleSearchChanged}
          size='small'
          value={search}
        />
        {loadingStores ? (
          <Loader />
        ) : (
          <div className={classes.columns}>
            {regions.map(({ region, stores: regionStores }) => {
              const selectedInRegion = regionStores.filter((store) =>
                selected.has(store.id),
              )

              return (
                <div className={classes.region} key={region}>
                  <div className={classes.regionHeader}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedInRegion.length === regionStores.length
                          }
                          indeterminate={
                            selectedInRegion.length > 0 &&
                            selectedInRegion.length < regionStores.length
                          }
                          onChange={toggleRegion(regionStores)}
                        />
                      }
                      label={<strong>{region}</strong>}
                    />
                    <Typography color='textSecondary' variant='caption'>
                      {`${selectedInRegion.length} of ${regionStores.length}`}
                    </Typography>
                  </div>
                  <div className={classes.stores}>
                    {regionStores.map((store) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.has(store.id)}
                            onChange={toggleStore(store.id)}
                          />
                        }
                        key={store.id}
                        label={store.name}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        {!loadingStores && regions.length === 0 && (
          <Typography className={classes.empty} color='textSecondary'>
            No stores match that filter.
          </Typography>
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Typography color='textSecondary' variant='caption'>
          {hasChanges
            ? [
                toAdd.length > 0 && `Adding ${toAdd.length}`,
                toRemove.length > 0 && `Removing ${toRemove.length}`,
              ]
                .filter(Boolean)
                .join(' · ')
            : 'No changes'}
        </Typography>
        <div>
          <Button onClick={onClose}>Close</Button>
          <Button disabled={!hasChanges} onClick={handleConfirm}>
            Save
          </Button>
        </div>
      </DialogActions>
    </ResponsiveDialog>
  )
}

export default ProductStoresDialog

type Props = {
  existingStoreIds: number[]
  loading?: boolean
  onClose: () => void
  onConfirm: (storeIds: number[]) => void
  open: boolean
}

const useStyles = makeStyles()((theme) => ({
  search: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    maxWidth: '320px',
  },
  columns: {
    columnGap: theme.spacing(4),
    [theme.breakpoints.up('sm')]: {
      columnCount: 2,
    },
    [theme.breakpoints.up('md')]: {
      columnCount: 3,
    },
    [theme.breakpoints.up('lg')]: {
      columnCount: 4,
    },
  },
  region: {
    // This one style `breakInside` works wonders; ensures every region exists within
    // a column without wrapping if possible
    breakInside: 'avoid',
    marginBottom: theme.spacing(2),
  },
  regionHeader: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
  },
  stores: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(2),
  },
  empty: {
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  actions: {
    justifyContent: 'space-between',
  },
}))

/**
 * Groups stores by region, sorting the regions and the stores within them by
 * name. Stores with no region land in a trailing catch-all group.
 */
const NO_REGION = 'No Region'
const groupByRegion = (stores: StoreSummary[]) => {
  const byRegion: Record<string, StoreSummary[]> = {}

  stores.forEach((store) => {
    const region = store.region || NO_REGION
    byRegion[region] = (byRegion[region] || []).concat(store)
  })

  return Object.keys(byRegion)
    .sort((a, b) => {
      if (a === NO_REGION) return 1
      if (b === NO_REGION) return -1
      return a.localeCompare(b)
    })
    .map((region) => ({
      region,
      stores: byRegion[region]
        .slice() // better than a spread to copy an array
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
}
